import React, { useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Play, MapPin, Clock, ShieldAlert, Lock, Check, HeartHandshake } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function VideoEvidenceModal({ event, open, onOpenChange }) {
  const { money } = useBranding();
  const qc = useQueryClient();
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);

  const review = async (status) => {
    try {
      await api.put(`/events/${event.id}/review`, { status });
      toast.success(status === "suspicious"
        ? "Marked suspicious — escalates to the owner if unmatched at end of day"
        : "Marked reviewed — legitimate");
      qc.invalidateQueries();
      onOpenChange(false);
    } catch { toast.error("Could not update review"); }
  };

  if (!event) return null;
  const clip = event.clip;
  const charitable = event.kind === "charitable";

  const play = () => {
    setPlaying(true);
    setTimeout(() => videoRef.current?.play().catch(() => {}), 50);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setPlaying(false); onOpenChange(v); }}>
      <DialogContent
        className="max-w-4xl w-[95vw] sm:w-full p-0 gap-0 bg-[#1E2638] border-[#2A3550] max-h-[90vh] overflow-y-auto"
        data-testid="video-evidence-modal"
      >
        <DialogTitle className="sr-only">
          Video protocol — Store #{event.store_number} {event.chair_label}
        </DialogTitle>
        <div className="grid md:grid-cols-2">
          {/* Video side */}
          <div className="relative bg-black min-h-[220px] md:min-h-[300px] flex items-center justify-center">
            {clip ? (
              !playing ? (
                <>
                  <img
                    src={clip.poster}
                    alt="chair footage"
                    className="absolute inset-0 h-full w-full object-cover"
                    style={{ filter: "blur(6px) brightness(0.7)" }}
                  />
                  <button
                    onClick={play}
                    data-testid="video-evidence-play-btn"
                    className="relative z-10 h-16 w-16 rounded-full bg-[#1E2638]/90 hover:bg-[#1E2638] flex items-center justify-center transition-colors duration-200"
                  >
                    <Play className="h-7 w-7 text-black ml-1" fill="black" />
                  </button>
                </>
              ) : (
                <video
                  ref={videoRef}
                  src={clip.video_url}
                  controls
                  loop
                  className="absolute inset-0 h-full w-full object-cover"
                  style={{ filter: "blur(3px)" }}
                />
              )
            ) : (
              <div className="text-slate-400 text-sm p-8 text-center">
                No footage retained — this service matched a sale.
              </div>
            )}

            {/* overlays */}
            <div className="absolute top-3 left-3 z-20 flex items-center gap-2 text-[11px]">
              <span className="px-2 py-1 rounded bg-black/70 text-red-400 font-mono flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-red-500/100 pulse-status" /> REC
              </span>
              {clip?.face_blurred && (
                <span className="px-2 py-1 rounded bg-black/70 text-zinc-200 flex items-center gap-1">
                  <Lock className="h-3 w-3" /> Faces blurred
                </span>
              )}
            </div>
            {clip && (
              <div className="absolute bottom-3 left-3 right-3 z-20 flex items-center justify-between gap-2 text-[11px] font-mono text-slate-300/80">
                <span className="truncate">{event.store_name} · {event.chair_label}</span>
                <span className="shrink-0">{fmtTime(clip.window_start)}</span>
              </div>
            )}
          </div>

          {/* Info side */}
          <div className="p-5 sm:p-7">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-300 font-semibold">
              {charitable ? <><HeartHandshake className="h-4 w-4 text-rose-400" /> Charitable service</> : <><ShieldAlert className="h-4 w-4" /> Video protocol</>}
            </div>
            <h2 className="mt-3 font-display text-2xl font-medium tracking-tight leading-snug">
              {charitable
                ? `Store #${event.store_number} — charitable cut, donated`
                : `Store #${event.store_number} — service delivered, no matching sale yet`}
            </h2>
            <p className="mt-2 text-3xl font-display font-light" style={{ color: charitable ? "#fb7185" : "var(--brand)" }}>
              {charitable ? `${money(event.expected_price)} donated` : `${money(event.expected_price)} to reconcile`}
            </p>

            <div className="mt-6 space-y-3 text-sm">
              <Row icon={MapPin} label="Location" value={`${event.store_name} · ${event.chair_label}`} />
              <Row icon={Clock} label="Service window" value={`${fmtTime(event.started_at)} · ${event.duration_min} min`} />
              <Row icon={ShieldAlert} label="Classification" value={event.classification.charAt(0).toUpperCase() + event.classification.slice(1)} />
            </div>

            <div className="mt-6 rounded-lg bg-[#171E2E] border border-[#2A3550] p-4 text-sm text-slate-300">
              {charitable
                ? <>{event.reason}. Footage is kept on charitable chairs so a free cut can't be used to hide an unrecorded paid service.</>
                : <>{event.reason}. The seat registered a full service but no matching POS transaction was found within the reconciliation window.</>}
            </div>

            {charitable ? (
              <div className="mt-6 rounded-lg border border-rose-400/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200 flex items-center gap-2" data-testid="charitable-note">
                <HeartHandshake className="h-4 w-4" /> Counted as community giving{event.beneficiary_name ? ` · ${event.beneficiary_name}` : ""} — excluded from revenue &amp; leakage.
              </div>
            ) : (
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => review("suspicious")}
                  data-testid="mark-suspicious-btn"
                  className="flex-1 flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-medium text-white transition-opacity duration-200 hover:opacity-90"
                  style={{ backgroundColor: "var(--brand)" }}
                >
                  <ShieldAlert className="h-4 w-4" /> Suspicious — cash to pocket
                </button>
                <button
                  onClick={() => review("reviewed_legit")}
                  data-testid="mark-legit-btn"
                  className="flex-1 flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-medium border border-[#334063] text-slate-100 hover:bg-[#232C40] transition-colors duration-200"
                >
                  <Check className="h-4 w-4" /> Legitimate
                </button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 text-slate-400 mt-0.5" />
      <div>
        <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400">{label}</div>
        <div className="text-slate-100">{value}</div>
      </div>
    </div>
  );
}
